import type {
  AnalysisBundle,
  DumpCapacityReport,
  ExecutableExitReport,
  FixtureSummary,
  ProviderStatus,
  RugCapacityReport,
  TokenIdentity,
  IdentityReport,
} from "./shared/report";

export interface CapabilityResponse {
  identity: TokenIdentity;
  identityReport: IdentityReport;
  rugCapacity: RugCapacityReport;
}

export interface DumpResponse {
  dumpCapacity: DumpCapacityReport;
}

export interface ExitResponse {
  identity: TokenIdentity;
  executableExit: ExecutableExitReport;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const SESSION_STORAGE_KEY = "rug-lens-provider-session";

export function getSessionId(): string {
  const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;
  const id =
    typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}_${Array.from(crypto.getRandomValues(new Uint32Array(4)))
          .map((value) => value.toString(36))
          .join("_")}`;
  sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  return id;
}

export async function fetchFixtures(): Promise<FixtureSummary[]> {
  const result = await apiFetch<{ fixtures: FixtureSummary[] }>("/api/fixtures");
  return result.fixtures;
}

export function fetchFixture(id: string): Promise<AnalysisBundle> {
  return apiFetch<AnalysisBundle>(`/api/fixtures/${encodeURIComponent(id)}`);
}

export function fetchProviderStatus(): Promise<ProviderStatus> {
  return apiFetch<ProviderStatus>("/api/provider-status");
}

export function analyzeCapabilities(address: string): Promise<CapabilityResponse> {
  return apiFetch<CapabilityResponse>("/api/analyze/capabilities", {
    method: "POST",
    body: JSON.stringify({ address }),
  });
}

export function analyzeExits(
  address: string,
  input: { usdAmounts?: number[]; rawTokenAmounts?: string[] },
): Promise<ExitResponse> {
  return apiFetch<ExitResponse>("/api/analyze/exits", {
    method: "POST",
    body: JSON.stringify({ address, ...input }),
  });
}

export function analyzeDump(address: string): Promise<DumpResponse> {
  return apiFetch<DumpResponse>("/api/analyze/dump", {
    method: "POST",
    body: JSON.stringify({ address }),
  });
}

export function saveSessionKeys(input: {
  heliusApiKey?: string;
  jupiterApiKey?: string;
}): Promise<{ ok: boolean }> {
  return apiFetch("/api/session-keys", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function forgetSessionKeys(): Promise<{ ok: boolean }> {
  return apiFetch("/api/session-keys", { method: "DELETE" });
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        "X-Rug-Lens-Session": getSessionId(),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError(
      "NETWORK",
      "Rug Lens couldn’t connect. Try again shortly.",
    );
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError("BAD_RESPONSE", "Rug Lens received an unexpected response. Try again shortly.");
  }

  if (!response.ok) {
    const error = payload as { code?: string; message?: string };
    throw new ApiError(
      error.code ?? `HTTP_${response.status}`,
      error.message ?? "Rug Lens could not complete that request.",
    );
  }
  return payload as T;
}
